import { memo, useEffect, useState } from "react";
import { useQuery } from "react-query";
import { ReservationService } from "../../services/ReservationService";
import { Badge, Button, Flex, Skeleton, Spin } from "antd";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { mdlReservationDetailRequest } from "../../models/service-models/reservation/ReservationDetailRequest";
import { CheckOutlined, CloseOutlined, DownloadOutlined, HourglassOutlined, PrinterOutlined } from '@ant-design/icons';
import { enmConfirmationStatus } from "../../models/enums/ConfirmationStatus";
import { enmQuotaType } from "../../models/enums/QuotaType";
import { InputHelper } from "../../utils/InputHelper";
import { CustomerTable } from "./CustomerTable";
import { enmReservationStatus } from "../../models/enums/ReservationStatus";
import { enmReservationType } from "../../models/enums/ReservationType";
import { mdlReservation } from "../../models/domain/Reservation";
import { ExtraServiceTable } from "./ExtraServiceTable";
import { SFModal } from "../elements/SFModal";
import { ReservationHistory } from "./ReservationHistory";
import { enmTourOperatorConfirmationStatus } from "../../models/enums/TourOperatorConfirmationStatus";

const disableReservationOperations = process.env.REACT_APP_DISABLE_RESERVATION_OPERATIONS === "true";

export type ReservationDetailProps = {
    //reservationId?: string;
    displayMode?: boolean;
    reservation?: mdlReservation;
    onConfirmClick?: Function;
    onRejectClick?: Function;
    onPrint?: Function;
    onExport?: Function;
}

enum enmConfirmationPropType {
    COLOR = 1,
    TEXT = 2
}

export const ReservationDetail = memo(function ReservationDetail(props: ReservationDetailProps) {
    const { t } = useTranslation();
    const [openReservationHistory, setOpenReservationHistory] = useState<boolean>(false);

    const {
        data,
        isLoading,
        isError,
        refetch
    } = useQuery(
        ["getReservationDetail", props.reservation?.reservationId],
        () => {
            if (props.reservation && props.reservation.reservationId && !props.displayMode) {
                var request: mdlReservationDetailRequest = { reservationId: props.reservation.reservationId!, agencyId: props.reservation.agencyId!, paxId: props.reservation.paximumId! };
                return ReservationService.Detail(request);
            }
        },
        {
            refetchOnWindowFocus: false,
            enabled: !!props.reservation?.reservationId,
            cacheTime: 0,
            staleTime: 0
        }
    );

    const getTuropConfStatusString = (reservation: mdlReservation) => {
        switch (reservation.tourOperatorConfirmationStatus) {
            case enmTourOperatorConfirmationStatus.Wait:
                return t('resTable.confirmationStatusWait');
            case enmTourOperatorConfirmationStatus.Yes:
                return t('resTable.confirmationStatusYes');
            case enmTourOperatorConfirmationStatus.No:
                return t('resTable.confirmationStatusNo');
            case enmTourOperatorConfirmationStatus.Sent:
                return t('resTable.confirmationStatusSent');
            case enmTourOperatorConfirmationStatus.NotSent:
                return t('resTable.confirmationStatusNotSent');
        }
    }

    let reservation = !props.displayMode ? data?.body?.reservation : props.reservation;

    const getConfirmationProps = (type: enmConfirmationPropType) => {
        switch (reservation?.confirmationStatus) {
            case enmConfirmationStatus.Yes:
                switch (type) {
                    case enmConfirmationPropType.COLOR:
                        return "green";
                    default:
                        return t("reservationDetail.confirmationStatusYes");
                }
            case enmConfirmationStatus.No:
                switch (type) {
                    case enmConfirmationPropType.COLOR:
                        return "red";
                    default:
                        return t("reservationDetail.confirmationStatusNo");
                }
            default:
                switch (type) {
                    case enmConfirmationPropType.COLOR:
                        return "gray";
                    default:
                        return t("reservationDetail.confirmationStatusWait");
                }
        }
    }

    const getQuotaTypeText = () => {
        if (reservation && reservation.quotaType != null) {
            var quotaText = enmQuotaType[reservation.quotaType];
            return t(`reservationDetail.quotaType${InputHelper.capitalizeFirstLetter(quotaText.toLowerCase())}`)
        }
    }

    const getResStatusText = () => {
        if (reservation && reservation.reservationStatus != null) {
            var resStatusText = enmReservationStatus[reservation.reservationStatus];
            return t(`reservationDetail.resStatus${InputHelper.capitalizeFirstLetter(resStatusText.toLowerCase())}`)
        }
    }

    const getResTypeText = () => {
        if (reservation && reservation.reservationType != null) {
            var resTypeText = enmReservationType[reservation.reservationType];
            return t(`reservationDetail.resType${InputHelper.capitalizeFirstLetter(resTypeText.toLowerCase())}`)
        }
    }

    const getDisabledStatus = (reservation: mdlReservation) => {
        return reservation.confirmationStatus == enmConfirmationStatus.No || reservation.confirmationStatus == enmConfirmationStatus.Yes;
    }

    const handleReservationHistoryOpen = () => {
        setOpenReservationHistory(!openReservationHistory);
    }

    const handleExport = (reservation?: mdlReservation) => {
        props.onExport && props.onExport(reservation);
    }

    const handlePrint = (reservation?: mdlReservation) => {
        props.onPrint && props.onPrint(reservation);
    }

    const getCalcInfoVisibility = (reservation: mdlReservation) => {
        return reservation.buyCalcInfo || reservation.buyCalc;
    }

    const handleHistoryToggle = () => {
        setOpenReservationHistory(!openReservationHistory);
    }



    return (
        <>
            {
                isLoading
                &&
                <Skeleton active />
            }
            {
                reservation
                &&
                <Badge.Ribbon text={getConfirmationProps(enmConfirmationPropType.TEXT)} color={getConfirmationProps(enmConfirmationPropType.COLOR)}>
                    <div className="card card-bordered">
                        <div className="card-inner">
                            <div className="team">
                                {
                                    reservation
                                        ?
                                        <div className="card-inner">
                                            <div className="row">
                                                <h6 className="overline-title mb-2">{t("reservationDetail.generalDetails")}</h6>
                                                <div className="row">
                                                    <div className="col-sm-6 col-md-3 col-lg-2">
                                                        <span
                                                            className="sub-text">{t("reservationDetail.hotelName")}</span>
                                                        <span>{reservation.hotelName}</span>
                                                    </div>
                                                    <div className="col-sm-6 col-md-3 col-lg-2">
                                                        <span
                                                            className="sub-text">{t("reservationDetail.voucherNo")}</span>
                                                        <span>{reservation.voucherNo}</span>
                                                    </div>
                                                    <div className="col-sm-6 col-md-3 col-lg-2">
                                                        <span
                                                            className="sub-text">{t("reservationDetail.agency")}</span>
                                                        <span>{reservation.agencyName}</span>
                                                    </div>
                                                    <div className="col-sm-6 col-md-3 col-lg-2">
                                                        <span
                                                            className="sub-text">{t("reservationDetail.tourOperatorName")}</span>
                                                        <span>{reservation.operator}</span>
                                                    </div>
                                                    <div className="col-sm-6 col-md-3 col-lg-2">
                                                        <span
                                                            className="sub-text">{t("reservationDetail.confirmationNumber")}</span>
                                                        <span>{reservation.confirmationNo ?? "-"}</span>
                                                    </div>
                                                    <div className="col-sm-6 col-md-3 col-lg-2">
                                                        <span
                                                            className="sub-text">{t("reservationDetail.sellDate")}</span>
                                                        <span>{reservation.sellDate ? dayjs(reservation.sellDate).format("DD.MM.YYYY") : "-"}</span>
                                                    </div>
                                                    <div className="col-sm-6 col-md-3 col-lg-2">
                                                        <span
                                                            className="sub-text">{t("reservationDetail.resStatus")}</span>
                                                        <span>{getResStatusText()}</span>
                                                    </div>
                                                    <div className="col-sm-6 col-md-3 col-lg-2">
                                                        <span
                                                            className="sub-text">{t("reservationDetail.resType")}</span>
                                                        <span>{getResTypeText()}</span>
                                                    </div>
                                                    <div className="col-sm-6 col-md-3 col-lg-2">
                                                        <span
                                                            className="sub-text">{t("reservationDetail.invoiceHeader")}</span>
                                                        <span>{reservation.invoiceHeader}</span>
                                                    </div>
                                                    <div className="col-sm-6 col-md-3 col-lg-2">
                                                        <span
                                                            className="sub-text">{t("reservationDetail.productName")}</span>
                                                        <span>{reservation.productName ?? '-'}</span>
                                                    </div>
                                                    {
                                                        reservation.tourOperatorConfirmationStatus != null &&
                                                        <div className="col-sm-6 col-md-3 col-lg-2">
                                                            <span
                                                                className="sub-text">{t("reservationDetail.touropConfStatus")}</span>
                                                            <span>{getTuropConfStatusString(reservation)}</span>
                                                        </div>
                                                    }

                                                </div>
                                            </div>
                                            <div className="row mt-35">
                                                <h6 className="overline-title mb-2">{t("reservationDetail.accomDetails")}</h6>
                                                <div className="row">
                                                    <div className="col-sm-6 col-md-3 col-lg-2">
                                                        <span
                                                            className="sub-text">{t("reservationDetail.checkIn")}</span>
                                                        <span>{dayjs(reservation.checkIn).format("DD.MM.YYYY")}</span>
                                                    </div>
                                                    <div className="col-sm-6 col-md-3 col-lg-2">
                                                        <span
                                                            className="sub-text">{t("reservationDetail.checkOut")}</span>
                                                        <span>{dayjs(reservation.checkOut).format("DD.MM.YYYY")}</span>
                                                    </div>
                                                    <div className="col-sm-6 col-md-3 col-lg-2">
                                                        <span className="sub-text">{t("reservationDetail.night")}</span>
                                                        <span>{reservation.nights}</span>
                                                    </div>
                                                    <div className="col-sm-6 col-md-3 col-lg-2">
                                                        <span className="sub-text">{t("reservationDetail.adult")}</span>
                                                        <span>{reservation.adults}</span>
                                                    </div>
                                                    <div className="col-sm-6 col-md-3 col-lg-2">
                                                        <span className="sub-text">{t("reservationDetail.child")}</span>
                                                        <span>{reservation.childs && reservation.childs > 0 ? reservation.childs : "-"}</span>
                                                    </div>
                                                    <div className="col-sm-6 col-md-3 col-lg-2">
                                                        <span
                                                            className="sub-text">{t("reservationDetail.infant")}</span>
                                                        <span>{reservation.infants && reservation.infants > 0 ? reservation.infants : "-"}</span>
                                                    </div>
                                                    <div className="col-sm-6 col-md-3 col-lg-2">
                                                        <span
                                                            className="sub-text">{t("reservationDetail.roomName")}</span>
                                                        <span>{reservation.roomName}</span>
                                                    </div>
                                                    <div className="col-sm-6 col-md-3 col-lg-2">
                                                        <span
                                                            className="sub-text">{t("reservationDetail.roomType")}</span>
                                                        <span>{reservation.roomTypeName}</span>
                                                    </div>
                                                    <div className="col-sm-6 col-md-3 col-lg-2">
                                                        <span className="sub-text">{t("reservationDetail.board")}</span>
                                                        <span>{reservation.accomName}</span>
                                                    </div>
                                                    <div className="col-sm-6 col-md-3 col-lg-2">
                                                        <span
                                                            className="sub-text">{t("reservationDetail.quotaType")}</span>
                                                        <span>{getQuotaTypeText()}</span>
                                                    </div>
                                                    <div className="col-sm-6 col-md-3 col-lg-2">
                                                        <span
                                                            className="sub-text">{t("reservationDetail.roomCount")}</span>
                                                        <span>{reservation.roomCount}</span>
                                                    </div>
                                                    <div className="col-sm-6 col-md-3 col-lg-2">
                                                        <span
                                                            className="sub-text">{t("reservationDetail.extraBed")}</span>
                                                        <span>{reservation.extraBed && reservation.extraBed > 0 ? reservation.extraBed : "-"}</span>
                                                    </div>
                                                    <div className="col-sm-6 col-md-3 col-lg-2">
                                                        <span
                                                            className="sub-text">{t("reservationDetail.totalPax")}</span>
                                                        <span>{reservation.pax}</span>
                                                    </div>

                                                </div>
                                            </div>
                                            <div className="row mt-35">
                                                <h6 className="overline-title mb-2">{t("reservationDetail.otherDetails")}</h6>
                                                <div className="row">
                                                    <div className="col-sm-6 col-md-4 col-lg-4">
                                                        <span
                                                            className="sub-text">{t("reservationDetail.operatorOriginalHotelCode")}</span>
                                                        <span>{reservation.originalHotelCode}</span>
                                                    </div>
                                                    {
                                                        reservation.soldPrice
                                                        &&
                                                        <div className="col-sm-6 col-md-4 col-lg-4">
                                                            <span
                                                                className="sub-text">{t("reservationDetail.accomPrice")}</span>
                                                            <span>{reservation.soldPrice} {reservation.soldCurrency}</span>
                                                        </div>
                                                    }
                                                    <div className="col-sm-6 col-md-6 col-lg-6">
                                                        <span
                                                            className="sub-text">{t("reservationDetail.agencyNote")}</span>
                                                        <span>{reservation.note ?? "-"}</span>
                                                    </div>
                                                    <div className="col-sm-6 col-md-6 col-lg-6">
                                                        <span
                                                            className="sub-text">{t("reservationDetail.confirmNote")}</span>
                                                        <span>{reservation.confirmeNote ?? '-'}</span>
                                                    </div>
                                                    {
                                                        reservation.confirmDate
                                                        &&
                                                        <div className="col-sm-6 col-md-6 col-lg-6">
                                                            <span
                                                                className="sub-text">{t("reservationDetail.confirmDate")}</span>
                                                            <span>{dayjs(reservation.confirmDate, "YYYY-MM-DD").format("DD.MM.YYYY")}</span>
                                                        </div>
                                                    }
                                                </div>
                                            </div>

                                            {
                                                getCalcInfoVisibility(reservation)
                                                &&
                                                <div className="row mt-35">
                                                    <div className="row">
                                                        <div className="col-sm-12 col-md-12 col-lg-12">
                                                            <span
                                                                className="sub-text">{t("reservationDetail.buyCalcInfo")}</span>
                                                            <span>{!InputHelper.isNullOrUndefinedOrEmpty(reservation.buyCalcInfo) ? reservation.buyCalcInfo : "-"}</span>
                                                        </div>
                                                        <div className="col-sm-12 col-md-12 col-lg-12">
                                                            <span
                                                                className="sub-text">{t("reservationDetail.buyCalc")}</span>
                                                            <span>{!InputHelper.isNullOrUndefinedOrEmpty(reservation.buyCalc) ? reservation.buyCalc : "-"}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            }

                                            {
                                                reservation.hasExtra && reservation.extraServices && reservation.extraServices.length > 0
                                                &&
                                                <div className="row  mt-35">
                                                    <h6 className="overline-title mb-2">{t("reservationDetail.extraServiceDetails")}</h6>
                                                    <ExtraServiceTable extraServices={reservation.extraServices} />
                                                </div>
                                            }
                                            <div className="row  mt-35">
                                                <h6 className="overline-title mb-2">{t("reservationDetail.customerDetails")}</h6>
                                                <CustomerTable checkInDate={reservation.checkIn}
                                                    customers={reservation.customers} />
                                            </div>
                                        </div>
                                        : t("reservationDetail.cantShowDetail")
                                }
                                <Flex gap="small" wrap justify="space-between">
                                    <Flex gap="small" wrap justify="flex-start">
                                        {
                                            !props.displayMode
                                            &&
                                            <Button type="primary" icon={<HourglassOutlined />} size="large"
                                                onClick={handleReservationHistoryOpen}>
                                                {t("reservationDetail.history")}
                                            </Button>
                                        }
                                        <Button type="primary" onClick={() => handlePrint(reservation)} icon={<PrinterOutlined />} size="large">
                                            {t("reservationDetail.print")}
                                        </Button>
                                        <Button type="primary" onClick={() => handleExport(reservation)} icon={<DownloadOutlined />} size="large">
                                            {t("reservationDetail.download")}
                                        </Button>
                                    </Flex>
                                    <Flex gap="small" wrap justify="flex-end">
                                        {

                                        }
                                        {
                                            !disableReservationOperations && (
                                                <>
                                                    <Button disabled={getDisabledStatus(reservation)} type="primary"
                                                        className="btn-success"
                                                        onClick={() => props.onConfirmClick ? props.onConfirmClick(reservation) : undefined}
                                                        icon={<CheckOutlined />} size="large">
                                                        {t("reservationDetail.confirm")}
                                                    </Button>
                                                    <Button disabled={getDisabledStatus(reservation)} type="primary" danger
                                                        onClick={() => props.onRejectClick ? props.onRejectClick(reservation) : undefined}
                                                        icon={<CloseOutlined />} size="large">
                                                        {t("reservationDetail.notConfirm")}
                                                    </Button>
                                                </>
                                            )
                                        }
                                    </Flex>
                                </Flex>
                            </div>
                        </div>
                    </div>
                </Badge.Ribbon>
            }
            <SFModal
                width={window.innerWidth * 85 / 100}
                open={openReservationHistory}
                toggler={() => {
                    handleHistoryToggle()
                }}
                body={<ReservationHistory reservationId={props.reservation?.reservationId} />}
                title={t("monitor.reservationHistory")}
            />
        </>

    )
})